// 월드 공간 이름표(Sprite): 시설 이름표와 NPC 머리 위 이름표가 같은 모양을 쓴다. 스프라이트라 항상 카메라를 본다.
// 매 프레임 placeLabels가 두 가지를 맞춘다(20261020-world-npc, 루프 D01):
//  - 시설·문 이름표가 카메라 쪽(컷어웨이 범위)이나 카메라 바로 앞에 오면 흐리게 한다. 회전 카메라에서 교관 건물 이름표가
//    화면 위쪽 절반을 덮던 결함(270도 캡처) 때문이다. NPC 이름표는 상호작용 대상이라 흐리지 않는다.
//  - 이름표가 보이는 DOM 패널(마을 의뢰 카드·지역 안내 카드, shared/panelRects) 밑으로 들어가면 화면에서 옆으로 비켜 그린다.
//    0도에서 미라·세르 이름표가 왼쪽 의뢰 카드에 잘려 '라 · 잡화상'으로 보이던 결함 때문이다. 월드 위치·판정은 바꾸지 않는다.
import * as THREE from 'three/webgpu';
import type { ScreenRect } from '../../shared/panelRects';
import { cutFactor } from './cut';

export type LabelKind = 'npc' | 'facility' | 'exit';
/** shift = 화면 가로 이동량(px, 부드럽게 따라감), off = 지금 위치에 더해 둔 월드 이동량(부모 회전·배율 없음 가정) */
interface LabelState { kind: LabelKind; shift: number; off: THREE.Vector3 }
const LIVE = new Set<THREE.Sprite>();

/** 둥근 남색 판 + accent 테두리·글자. w = 월드 너비(m), 높이는 w/4 */
export function labelSprite(text: string, accent: string, w: number, kind: LabelKind = 'facility'): THREE.Sprite {
  const c = document.createElement('canvas');
  c.width = 384; c.height = 96;
  const g = c.getContext('2d')!;
  g.fillStyle = 'rgba(26,34,56,0.86)';
  g.strokeStyle = accent;
  g.lineWidth = 5;
  g.beginPath();
  g.roundRect(6, 10, 372, 76, 18);
  g.fill();
  g.stroke();
  g.fillStyle = accent;
  g.font = '900 ' + (text.length > 9 ? 34 : 44) + 'px system-ui, "Malgun Gothic", sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, 192, 50);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, fog: false }));
  sp.scale.set(w, w / 4, 1);
  // NPC 이름표는 상호작용 대상 표시라 앞쪽 깃대·가로등에 글자가 끊기지 않게 깊이 검사 없이 맨 위에 그린다(4방향 캡처 0026: 깃대가 '대'를 가림)
  if (kind === 'npc') { sp.material.depthTest = false; sp.renderOrder = 20; }
  sp.userData.label = { kind, shift: 0, off: new THREE.Vector3() } satisfies LabelState;
  LIVE.add(sp);
  return sp;
}

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const P = new THREE.Vector3(), Q = new THREE.Vector3(), RIGHT = new THREE.Vector3();
let lastMs = 0;

function shown(o: THREE.Object3D): boolean {
  for (let n: THREE.Object3D | null = o; n; n = n.parent) if (!n.visible) return false;
  return true;
}

/** 카메라 쪽 시설·문 이름표의 불투명도(0..1). k = 컷어웨이 정도, d = 카메라 거리(m) */
export function labelFade(k: number, d: number): number {
  return Math.max(0, 1 - k) * smooth(4, 8, d);
}

/**
 * 이름표 상자 [x0, x1](화면 px, 세로 y0..y1)를 예약 사각형과 겹치지 않게 옮기는 가로 이동량(px).
 * 오른쪽·왼쪽 중 화면 안에 남는 쪽에서 덜 움직이는 쪽을 고른다. 겹치지 않으면 0.
 */
export function avoidShift(x0: number, x1: number, y0: number, y1: number, width: number, reserved: readonly ScreenRect[]): number {
  let total = 0;
  for (let pass = 0; pass < 2; pass++) {
    let moved = false;
    for (const r of reserved) {
      if (x1 + total <= r.x || x0 + total >= r.x + r.w || y1 <= r.y || y0 >= r.y + r.h) continue;
      const right = r.x + r.w + 6 - (x0 + total), left = r.x - 6 - (x1 + total);
      const okR = x1 + total + right <= width, okL = x0 + total + left >= 0;
      total += okR && (!okL || right <= -left) ? right : okL ? left : right;
      moved = true;
    }
    if (!moved) break;
  }
  return total;
}

/** 매 프레임: 카메라·영웅 위치가 정해진 뒤 부른다. width·height = 캔버스 CSS px(뷰포트 기준) */
export function placeLabels(camera: THREE.Camera, width: number, height: number, hx: number, hz: number, reserved: readonly ScreenRect[]): void {
  const now = performance.now(), dt = lastMs ? Math.min(0.1, (now - lastMs) / 1000) : 0;
  lastMs = now;
  RIGHT.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
  for (const sp of LIVE) {
    if (!sp.parent) { LIVE.delete(sp); continue; }
    if (!shown(sp)) continue;
    const st = sp.userData.label as LabelState;
    const mat = sp.material as THREE.SpriteMaterial;
    // 지난 프레임에 더한 이동을 빼서 원래 자리(다른 코드가 바꾼 높이 포함)에서 다시 계산한다
    sp.position.sub(st.off);
    sp.getWorldPosition(P);
    if (st.kind !== 'npc') mat.opacity = labelFade(cutFactor(P.x, P.z, hx, hz), camera.position.distanceTo(P));
    let target = 0, hw = 0;
    Q.copy(P).project(camera);
    if (Q.z > -1 && Q.z < 1) {
      const sx = (Q.x + 1) / 2 * width, sy = (1 - Q.y) / 2 * height;
      Q.copy(P).addScaledVector(RIGHT, sp.scale.x / 2).project(camera);
      hw = Math.abs((Q.x + 1) / 2 * width - sx);
      const hh = hw * sp.scale.y / sp.scale.x;
      if (hw > 1 && reserved.length) target = avoidShift(sx - hw, sx + hw, sy - hh, sy + hh, width, reserved);
    }
    st.shift += (target - st.shift) * Math.min(1, dt * 12);
    if (Math.abs(st.shift - target) < 0.5) st.shift = target;
    // 화면 px 이동을 이름표 깊이의 월드 길이로 바꿔 카메라 오른쪽 방향으로 옮긴다(hw px = 폭의 절반 scale.x/2 m).
    // sprite.center는 WebGPU 스프라이트 재질에서 프레임마다 반영되지 않아(실측 2026-10-20) 위치를 옮긴다.
    st.off.copy(RIGHT).multiplyScalar(hw > 1 ? (st.shift / hw) * (sp.scale.x / 2) : 0);
    sp.position.add(st.off);
  }
}
